import { useCallback, useEffect, useMemo, useState } from 'react';
import { localized } from '@platform/core/lib/localized';
import { createModuleStrings } from '@platform/core/lib/moduleStrings';
import { statusColor } from '@platform/core/lib/statusColors';
import { moduleBundle } from '@platform/i18n';
import { findModule, findModuleDefinition, loadModule } from '@platform/modularity';
import { useGetCatalogQuery } from '@platform/state/api';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { kindFieldOf } from '../../form/header/kindFieldOf';
import type { IForeignRecords } from './IForeignRecords';

const GREY = 'oklch(0.7 0.01 260)';

/**
 * Підписи записів інших модулів (прототип v4 показує статус пов'язаного запису мовою інтерфейсу з крапкою й однину типу в чипі):
 * пакети цих модулів довантажуються (рядки й визначення), назви модулів — з каталогу; до завантаження — значення як є. Функції читають
 * реєстри під час виклику, тож після довантаження досить перерендеру (лічильник у стані).
 */
export function useForeignRecords(ws: string, modules: readonly string[]): IForeignRecords {
  const lang = useAppSelector(selectLang);
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws }, { skip: !ws });
  const [, setLoaded] = useState(0);
  const key = useMemo(() => [...new Set(modules)].filter(Boolean).sort().join(','), [modules]);
  useEffect(() => {
    let alive = true;
    const manifests = key.split(',').filter(Boolean).map((k) => findModule(k)).filter((m): m is NonNullable<typeof m> => Boolean(m));
    void Promise.all(manifests.map((m) => loadModule(m, lang).catch(() => undefined))).then(() => { if (alive) setLoaded((n) => n + 1); });
    return () => { alive = false; };
  }, [key, lang]);
  const stringsOf = useCallback((module: string) => createModuleStrings(module, lang, () => moduleBundle(lang, module)), [lang]);
  const typeOf = useCallback((module: string, rt: string) => findModuleDefinition(module)?.recordTypes.find((r) => r.key === rt), []);
  const foreignTypeLabel = useCallback((module: string, rt: string) => {
    const def = typeOf(module, rt);
    const summary = catalog?.modules.find((m) => m.key === module)?.recordTypes.find((r) => r.key === rt);
    return stringsOf(module).entity(rt, 'singular', def?.singular ?? def?.name ?? summary?.name ?? rt);
  }, [typeOf, catalog, stringsOf]);
  return useMemo((): IForeignRecords => ({
    status: (module, rt, value) => {
      const def = typeOf(module, rt);
      const own = def?.statuses.list.find((s) => s.value === value);
      return { label: stringsOf(module).status(rt, value, own?.label ?? value), color: def ? statusColor(def.statuses, value, rt) : GREY };
    },
    typeLabel: foreignTypeLabel,
    moduleLabel: (module) => {
      const summary = catalog?.modules.find((m) => m.key === module);
      return stringsOf(module).moduleName(summary ? summary.name : module) || (summary ? localized(summary.name, lang) : module);
    },
    kindLabel: (module, rt, values) => {
      const def = typeOf(module, rt);
      const kind = def ? kindFieldOf(def) : undefined;
      const value = kind ? values[kind.key] : undefined;
      if (!kind || typeof value !== 'string' || !value) return foreignTypeLabel(module, rt);
      return stringsOf(module).option(rt, kind.key, value, kind.options?.find((o) => o.value === value)?.label ?? value);
    },
  }), [stringsOf, typeOf, catalog, lang, foreignTypeLabel]);
}
